﻿import {nanoid, StringNamedValue} from "@2d-front-monorepo/core";
import React, {ChangeEvent, JSX, ReactNode, useMemo, useRef} from "react";
import {useArrayOnChange} from "../helpers/useArrayOnChange";
import {useSelected} from "../helpers/useSelected";

export interface OptionGroupEntry extends StringNamedValue {
	disabled?: boolean;
}

export interface OptionGroupProps {
	label: ReactNode,
	name: string,
	values: string[],
	items: OptionGroupEntry[];
	column?: string,
	disabled?: boolean;
	hidden?: boolean;
	onChange?: (values: number[]) => void;
	onBlur?: () => void;
	ref?: React.RefObject<HTMLSpanElement | null>;
	multiple?: boolean;
}

interface RenderItemProps {
	label: ReactNode;
	name: string;
	value: string;
	arrayKey: string;
	selected: boolean;
	type: "checkbox" | "radio";
	disabled?: boolean;
	itemDisabled?: boolean;
	onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
	onBlur?: () => void;
}

function RenderItem(props: RenderItemProps): JSX.Element {
	const {label, name, value, arrayKey, selected, type, disabled, itemDisabled, onChange, onBlur} = props;
	const controlId = useMemo(() => "cbg-" + nanoid(), []);
	const isDisabled = disabled || itemDisabled;
	return <>
		<input type={type}
		       id={controlId}
		       name={name}
		       value={value}
		       data-array-key={arrayKey}
		       checked={selected}
		       tabIndex={isDisabled ? -1 : undefined}
		       disabled={isDisabled}
		       onChange={onChange}
		       onBlur={onBlur}/>
		<label htmlFor={controlId}>{label}</label>
	</>;
}

export default function OptionGroup(props: OptionGroupProps): JSX.Element {
	const {
		label,
		name,
		values,
		items,
		column,
		hidden,
		disabled,
		onChange,
		onBlur,
		ref,
		multiple
	} = props;

	const klass = useMemo(() => "labelled-field" + (column ? ` ${column}` : ""), [column]);
	const type = multiple ? "checkbox" : "radio";
	const role = multiple ? "group" : "radiogroup";
	const fallbackRef = useRef<HTMLSpanElement>(null);
	const innerRef = ref ?? fallbackRef;

	const selected = useSelected(values, multiple);
	const internalOnChange = useArrayOnChange(innerRef, onChange);

	if (hidden) {
		return <></>;
	}

	return <div className={klass}>
		<div className="form__field">
			<label className="field__label">
				<span className="label__text">{label}</span>
			</label>
			<span className="field__options" role={role} ref={innerRef}>
				{items.map((item, index) => <RenderItem label={item.label}
				                                        value={item.value}
				                                        arrayKey={index.toString()}
				                                        key={index}
				                                        name={name}
				                                        selected={selected(item.value)}
				                                        type={type}
				                                        disabled={disabled}
				                                        itemDisabled={item.disabled}
				                                        onChange={internalOnChange}
				                                        onBlur={onBlur}/>)}
			</span>
		</div>
	</div>;
};
